// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Trends page flight log: card-per-flight layout by default with the dense
// score table behind a Layout toggle (Phase 5f). Mode (all/contributing/
// attention) and sort (date/focus/overall) state controls, the per-row
// "focus column" highlighting, the renderers (with cached signature so they
// only rebuild when rows or display options change), and the click-through
// to openTrendFlight.
//
// Exposed as a factory because the cluster reads/writes the trend
// mode + sort + layout mutable globals, mutates the rendered-signature
// cache, and calls many late-bound helpers (trendAffectedRows,
// renderCurrentTrendView, openTrendFlight, trendRowsHaveTurbo / Carb
// from the trend view renderer, etc.).

export function createTrendEvidenceTable({
  escapeHtml,
  scoreClass,
  formatTrendValue,
  buildTrendSignalSummaries,
  selectedTrendSignal,
  trendAffectedRows,
  trendStoryMetricKey,
  trendMetricMeta,
  trendRowsHaveTurbo,
  trendRowsHaveCarbIce,
  renderCurrentTrendView,
  openTrendFlight,
  getActiveTrendMetric,
  getActiveTrendEvidenceMode,
  setActiveTrendEvidenceMode,
  getActiveTrendEvidenceSort,
  setActiveTrendEvidenceSort,
  getActiveTrendEvidenceLayout,
  setActiveTrendEvidenceLayout,
  getRenderedTrendTableSignature,
  setRenderedTrendTableSignature,
}) {
  function trendTableMetricColumnKey(metricKey) {
    return {
      electricalScore: "chargingScore",
    }[metricKey] ?? metricKey;
  }

  function trendTableCellClass(row, key, focusedColumnKey) {
    return `trend-score ${scoreClass(row[key])}${key === focusedColumnKey ? " is-focused-column" : ""}`;
  }

  function trendTableFocusSignal(rows) {
    if (getActiveTrendMetric() === "overallScore") return buildTrendSignalSummaries(rows)[0] ?? null;
    return selectedTrendSignal(rows);
  }

  function setTrendEvidenceMode(mode) {
    if (!["all", "contributing", "attention"].includes(mode) || getActiveTrendEvidenceMode() === mode) return;
    setActiveTrendEvidenceMode(mode);
    renderCurrentTrendView();
  }

  function setTrendEvidenceSort(sort) {
    if (!["date", "focus", "overall"].includes(sort) || getActiveTrendEvidenceSort() === sort) return;
    setActiveTrendEvidenceSort(sort);
    renderCurrentTrendView();
  }

  function setTrendEvidenceLayout(layout) {
    if (!["cards", "table"].includes(layout) || getActiveTrendEvidenceLayout() === layout) return;
    setActiveTrendEvidenceLayout(layout);
    renderCurrentTrendView();
  }

  function trendEvidenceScoreSort(aValue, bValue) {
    const aFinite = Number.isFinite(aValue);
    const bFinite = Number.isFinite(bValue);
    if (aFinite && bFinite) return aValue - bValue;
    if (aFinite) return -1;
    if (bFinite) return 1;
    return 0;
  }

  function trendEvidenceAttentionRows(rows, focusedColumnKey) {
    return rows.filter((row) => {
      if (row.error) return false;
      const focusValue = focusedColumnKey ? row[focusedColumnKey] : null;
      if (Number.isFinite(focusValue)) return focusValue < 88;
      return Number.isFinite(row.overallScore) && row.overallScore < 88;
    });
  }

  function trendEvidenceRowsForMode(rows, focusedRowIds, focusedColumnKey) {
    const mode = getActiveTrendEvidenceMode();
    if (mode === "contributing") return rows.filter((row) => !row.error && focusedRowIds.has(row.id));
    if (mode === "attention") return trendEvidenceAttentionRows(rows, focusedColumnKey);
    return rows;
  }

  function sortTrendEvidenceRows(rows, focusedColumnKey) {
    const sorted = [...rows];
    const sort = getActiveTrendEvidenceSort();
    if (sort === "focus") {
      sorted.sort((a, b) => trendEvidenceScoreSort(a?.[focusedColumnKey], b?.[focusedColumnKey]) || (a.dateValue ?? 0) - (b.dateValue ?? 0));
      return sorted;
    }
    if (sort === "overall") {
      sorted.sort((a, b) => trendEvidenceScoreSort(a?.overallScore, b?.overallScore) || (a.dateValue ?? 0) - (b.dateValue ?? 0));
      return sorted;
    }
    sorted.sort((a, b) => (a.dateValue ?? 0) - (b.dateValue ?? 0) || String(a.title ?? "").localeCompare(String(b.title ?? "")));
    return sorted;
  }

  function updateTrendEvidenceControlState(rows, focusedRowIds, focusedColumnKey) {
    const hasRows = rows.some((row) => !row.error);
    const hasContributingRows = focusedRowIds.size > 0;
    const hasAttentionRows = trendEvidenceAttentionRows(rows, focusedColumnKey).length > 0;
    const mode = getActiveTrendEvidenceMode();
    const sort = getActiveTrendEvidenceSort();
    document.querySelectorAll("[data-trend-table-mode]").forEach((button) => {
      const buttonMode = button.dataset.trendTableMode;
      const active = buttonMode === mode;
      const disabled = !hasRows || (buttonMode === "contributing" && !hasContributingRows && !active) || (buttonMode === "attention" && !hasAttentionRows && !active);
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
      button.disabled = disabled;
    });
    document.querySelectorAll("[data-trend-table-sort]").forEach((button) => {
      const active = button.dataset.trendTableSort === sort;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
      button.disabled = !hasRows;
    });
    const layout = getActiveTrendEvidenceLayout();
    document.querySelectorAll("[data-trend-table-layout]").forEach((button) => {
      const active = button.dataset.trendTableLayout === layout;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  function updateTrendTableFocusState(rows, focusedColumnKey, focusSignal, shownRows = rows) {
    const focus = document.querySelector("#trendEvidenceFocus");
    const metricLabel = trendMetricMeta[getActiveTrendMetric()]?.label ?? "Selected area";
    if (focus) {
      const focusRows = trendAffectedRows(focusSignal).length;
      const mode = getActiveTrendEvidenceMode();
      const shownCount = mode === "all" ? "" : ` · ${shownRows.filter((row) => !row.error).length} shown`;
      const suffix = `${focusRows ? ` · ${focusRows} contributing flight${focusRows === 1 ? "" : "s"}` : ""}${shownCount}`;
      focus.textContent = `Table focus: ${metricLabel}${suffix}`;
    }
    document.querySelectorAll(".trend-table [data-trend-field]").forEach((element) => {
      element.classList.toggle("is-focused-column", Boolean(focusedColumnKey) && element.dataset.trendField === focusedColumnKey);
    });
  }

  function bindTrendTableEvents(body) {
    if (body.dataset.trendTableBound === "true") return;
    body.dataset.trendTableBound = "true";
    body.addEventListener("click", (event) => {
      const row = event.target.closest("[data-flight-id]");
      if (!row || !body.contains(row)) return;
      openTrendFlight(row.dataset.flightId, { source: "trendEvidence" });
    });
    body.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const row = event.target.closest("[data-flight-id]");
      if (!row || !body.contains(row)) return;
      event.preventDefault();
      openTrendFlight(row.dataset.flightId, { source: "trendEvidence" });
    });
  }

  function updateTrendTableRowFocusInPlace(body, focusedRowIds) {
    body.querySelectorAll("[data-flight-id]").forEach((element) => {
      element.classList.toggle("is-focus-row", focusedRowIds.has(element.dataset.flightId));
    });
  }

  // --- Flight-log cards (Phase 5f) ------------------------------------------------------------
  // The per-flight plain language comes from data the trend rows already carry: findingTitle is
  // the flight's primary finding sentence (summary.finding.title) and findingClass its overall
  // score class. The what-to-do line is synthesized from severity here — finding actions are NOT
  // stored in trend rows, and adding them would mean dual-emitting the field in both
  // trend-row-builder and the DB snapshot builder plus an ANALYSIS_VERSION bump.

  const CARD_ACTION_TEXT = Object.freeze({
    green: "Nothing to act on — this flight strengthens the baseline.",
    amber: "Worth a look: open this flight and compare the flagged area with a recent good flight in the same phase.",
    red: "Open this flight and compare the flagged area's traces with a recent similar flight; if it repeats across flights, bring it to your A&P.",
    muted: "Not enough scored data — if this repeats, check that the engine monitor recorded the full flight.",
  });

  const CARD_CHIP_KEYS = Object.freeze([
    "turboScore", "chtScore", "egtScore", "valveScore", "combustionScore",
    "descentEgtDropoutScore", "inductionLeakScore", "magScore", "carbIceScore", "coolingScore",
    "coolingRateScore", "chargingScore",
  ]);

  // The focused metric's chip always shows (it is what the table view would highlight), followed
  // by the flight's lowest-scoring areas, capped at three chips total.
  function trendCardScoreChips(row, focusedColumnKey) {
    const orderedKeys = [];
    if (CARD_CHIP_KEYS.includes(focusedColumnKey) && Number.isFinite(row[focusedColumnKey])) {
      orderedKeys.push(focusedColumnKey);
    }
    CARD_CHIP_KEYS
      .filter((key) => key !== focusedColumnKey && Number.isFinite(row[key]))
      .sort((a, b) => row[a] - row[b])
      .forEach((key) => orderedKeys.push(key));
    return orderedKeys.slice(0, 3).map((key) => `
      <span class="trend-score ${scoreClass(row[key])}${key === focusedColumnKey ? " is-focused-column" : ""}">${escapeHtml(trendMetricMeta[key]?.label ?? key)} ${formatTrendValue(row[key], key)}</span>
    `).join("");
  }

  function renderTrendFlightCard(row, focusedRowIds, focusedColumnKey) {
    const ariaLabel = `Open graph evidence for ${row.title} on ${row.dateLabel}`;
    if (row.error) {
      return `
        <article class="trend-flight-card status-muted is-error" data-flight-id="${escapeHtml(row.id)}" role="button" tabindex="0" aria-label="${escapeHtml(ariaLabel)}">
          <header class="trend-flight-card-top">
            <strong>${escapeHtml(row.title)}</strong>
            <span class="trend-flight-card-date">${escapeHtml(row.dateLabel)}</span>
            <em class="engine-status muted">Error</em>
          </header>
          <p class="trend-flight-card-finding">Unable to analyze: ${escapeHtml(row.error)}</p>
        </article>
      `;
    }
    const statusClass = scoreClass(row.overallScore);
    const focusClass = focusedRowIds.has(row.id) ? " is-focus-row" : "";
    const rawFindingTitle = String(row.findingTitle ?? "").trim();
    const hasFinding = Boolean(rawFindingTitle) && !/^no finding$/i.test(rawFindingTitle);
    const findingTitle = hasFinding ? rawFindingTitle : "No repeating cues in this flight";
    let findingMeta = hasFinding ? String(row.findingMeta ?? "").trim() : "";
    // The meta usually restates the title ("Oil system · Oil system scored 83 (Watch).") — drop
    // the duplicate prefix so the card reads as one sentence.
    if (findingMeta.toLowerCase().startsWith(findingTitle.toLowerCase())) {
      findingMeta = findingMeta.slice(findingTitle.length).replace(/^[\s:,-]+/, "");
    }
    // When a finding exists, the what-to-do line keys off that finding's severity word (the
    // finding reflects the flight's worst area, not the overall score). A flight with no
    // surfaced finding stays quiet — sub-metric dips without a finding are the trend signals'
    // job to escalate, not each card's.
    const finiteScores = [row.overallScore, ...CARD_CHIP_KEYS.map((key) => row[key])].filter(Number.isFinite);
    const actionClass = !hasFinding
      ? (finiteScores.length ? "green" : "muted")
      : /\b(Review|Attention)\b/.test(findingMeta) ? "red"
        : /\bWatch\b/.test(findingMeta) ? "amber"
          : finiteScores.length ? scoreClass(Math.min(...finiteScores)) : "muted";
    return `
      <article class="trend-flight-card status-${escapeHtml(statusClass)}${focusClass}" data-flight-id="${escapeHtml(row.id)}" role="button" tabindex="0" aria-label="${escapeHtml(ariaLabel)}">
        <header class="trend-flight-card-top">
          <strong>${escapeHtml(row.title)}</strong>
          <span class="trend-flight-card-date">${escapeHtml(row.dateLabel)}</span>
          <em class="engine-status ${escapeHtml(statusClass)}${focusedColumnKey === "overallScore" ? " is-focused-column" : ""}">Overall ${formatTrendValue(row.overallScore, "overallScore")}</em>
        </header>
        <p class="trend-flight-card-finding">${escapeHtml(findingTitle)}${findingMeta ? ` <span class="trend-flight-card-meta">${escapeHtml(findingMeta)}</span>` : ""}</p>
        <p class="trend-flight-card-action">${CARD_ACTION_TEXT[actionClass]}</p>
        <div class="trend-flight-card-scores">
          ${trendCardScoreChips(row, focusedColumnKey)}
        </div>
      </article>
    `;
  }

  function renderTrendTable(rows) {
    const body = document.querySelector("#trendTableBody");
    if (!body) return;
    bindTrendTableEvents(body);
    const cards = document.querySelector("#trendFlightCards");
    if (cards) bindTrendTableEvents(cards);
    const tableWrap = document.querySelector(".trend-table-wrap");
    const layout = cards ? getActiveTrendEvidenceLayout() : "table";
    if (cards) cards.hidden = layout !== "cards";
    if (tableWrap) tableWrap.hidden = layout === "cards" && Boolean(cards);
    const usableRows = rows.filter((row) => !row.error);
    const activeMetric = getActiveTrendMetric();
    const focusedColumnKey = trendTableMetricColumnKey(trendStoryMetricKey(activeMetric));
    const focusSignal = trendTableFocusSignal(usableRows);
    const focusedRowIds = new Set(trendAffectedRows(focusSignal).map((row) => row.id).filter(Boolean));
    const displayRows = sortTrendEvidenceRows(trendEvidenceRowsForMode(rows, focusedRowIds, focusedColumnKey), focusedColumnKey);
    const evidenceCount = document.querySelector("#trendEvidenceCount");
    const mode = getActiveTrendEvidenceMode();
    const sort = getActiveTrendEvidenceSort();
    if (evidenceCount) {
      const shownUsableRows = displayRows.filter((row) => !row.error);
      evidenceCount.textContent = rows.length
        ? mode === "all"
          ? `${usableRows.length} scored flight${usableRows.length === 1 ? "" : "s"}${rows.length !== usableRows.length ? `, ${rows.length - usableRows.length} with errors` : ""}`
          : `${shownUsableRows.length} of ${usableRows.length} scored flight${usableRows.length === 1 ? "" : "s"} shown`
        : "No evidence rows yet";
    }
    const showTurbo = trendRowsHaveTurbo(usableRows);
    const showCarbIce = trendRowsHaveCarbIce(usableRows);
    const columnCount = 15 + (showTurbo ? 1 : 0) + (showCarbIce ? 1 : 0);
    if (!rows.length) {
      body.innerHTML = `<tr><td colspan="${columnCount}">No trend-ready flights available.</td></tr>`;
      if (cards) cards.innerHTML = `<div class="trend-empty-state"><span class="trend-empty-state-icon" aria-hidden="true">◌</span><strong>No trend-ready flights</strong><p>Upload engine-monitor flights for this aircraft to build its flight log.</p></div>`;
      setRenderedTrendTableSignature(`empty:${layout}:${columnCount}`);
      updateTrendEvidenceControlState(rows, focusedRowIds, focusedColumnKey);
      updateTrendTableFocusState(usableRows, focusedColumnKey, focusSignal, []);
      return;
    }
    if (!displayRows.length) {
      body.innerHTML = `<tr><td colspan="${columnCount}">No flights match this table view.</td></tr>`;
      if (cards) cards.innerHTML = `<div class="trend-empty-state"><span class="trend-empty-state-icon" aria-hidden="true">◌</span><strong>No flights match this view</strong><p>Change the Show filter above to see more of this aircraft's flights.</p></div>`;
      setRenderedTrendTableSignature(`none:${layout}:${columnCount}`);
      updateTrendEvidenceControlState(rows, focusedRowIds, focusedColumnKey);
      updateTrendTableFocusState(usableRows, focusedColumnKey, focusSignal, displayRows);
      return;
    }
    // Card markup bakes in the focused column's chips, so the cards signature must change when
    // the focused metric changes (the table only restyles cells in place, hence no key there).
    const skeletonSignature = layout === "cards"
      ? `cards:${mode}|${sort}|${focusedColumnKey}|${displayRows.map((row) => row.id).join(",")}`
      : `rows:${mode}|${sort}|${showTurbo ? "T" : ""}|${showCarbIce ? "C" : ""}|${displayRows.map((row) => row.id).join(",")}`;
    if (getRenderedTrendTableSignature() === skeletonSignature) {
      updateTrendTableRowFocusInPlace(layout === "cards" ? cards : body, focusedRowIds);
      updateTrendEvidenceControlState(rows, focusedRowIds, focusedColumnKey);
      updateTrendTableFocusState(usableRows, focusedColumnKey, focusSignal, displayRows);
      return;
    }
    if (layout === "cards") {
      cards.innerHTML = displayRows.map((row) => renderTrendFlightCard(row, focusedRowIds, focusedColumnKey)).join("");
      setRenderedTrendTableSignature(skeletonSignature);
      updateTrendEvidenceControlState(rows, focusedRowIds, focusedColumnKey);
      updateTrendTableFocusState(usableRows, focusedColumnKey, focusSignal, displayRows);
      return;
    }
    body.innerHTML = displayRows.map((row) => {
      if (row.error) {
        return `
          <tr data-flight-id="${escapeHtml(row.id)}" role="button" tabindex="0" aria-label="Open graph evidence for ${escapeHtml(row.title)} on ${escapeHtml(row.dateLabel)}">
            <td>${escapeHtml(row.dateLabel)}</td>
            <td>${escapeHtml(row.title)}</td>
            <td colspan="${columnCount - 2}">Unable to analyze: ${escapeHtml(row.error)}</td>
          </tr>
        `;
      }
      const focusClass = focusedRowIds.has(row.id) ? " is-focus-row" : "";
      return `
        <tr class="${focusClass.trim()}" data-flight-id="${escapeHtml(row.id)}" role="button" tabindex="0" aria-label="Open graph evidence for ${escapeHtml(row.title)} on ${escapeHtml(row.dateLabel)}">
          <td>${escapeHtml(row.dateLabel)}</td>
          <td>${escapeHtml(row.title)}</td>
          <td data-trend-field="overallScore" class="${trendTableCellClass(row, "overallScore", focusedColumnKey)}">${formatTrendValue(row.overallScore, "overallScore")}</td>
          ${showTurbo ? `<td data-trend-field="turboScore" class="${trendTableCellClass(row, "turboScore", focusedColumnKey)}">${formatTrendValue(row.turboScore, "turboScore")}</td>` : ""}
          <td data-trend-field="chtScore" class="${trendTableCellClass(row, "chtScore", focusedColumnKey)}">${formatTrendValue(row.chtScore, "chtScore")}</td>
          <td data-trend-field="egtScore" class="${trendTableCellClass(row, "egtScore", focusedColumnKey)}">${formatTrendValue(row.egtScore, "egtScore")}</td>
          <td data-trend-field="valveScore" class="${trendTableCellClass(row, "valveScore", focusedColumnKey)}">${formatTrendValue(row.valveScore, "valveScore")}</td>
          <td data-trend-field="combustionScore" class="${trendTableCellClass(row, "combustionScore", focusedColumnKey)}">${formatTrendValue(row.combustionScore, "combustionScore")}</td>
          <td data-trend-field="descentEgtDropoutScore" class="${trendTableCellClass(row, "descentEgtDropoutScore", focusedColumnKey)}">${formatTrendValue(row.descentEgtDropoutScore, "descentEgtDropoutScore")}</td>
          <td data-trend-field="inductionLeakScore" class="${trendTableCellClass(row, "inductionLeakScore", focusedColumnKey)}">${formatTrendValue(row.inductionLeakScore, "inductionLeakScore")}</td>
          <td data-trend-field="magScore" class="${trendTableCellClass(row, "magScore", focusedColumnKey)}">${formatTrendValue(row.magScore, "magScore")}</td>
          ${showCarbIce ? `<td data-trend-field="carbIceScore" class="${trendTableCellClass(row, "carbIceScore", focusedColumnKey)}">${formatTrendValue(row.carbIceScore, "carbIceScore")}</td>` : ""}
          <td data-trend-field="coolingScore" class="${trendTableCellClass(row, "coolingScore", focusedColumnKey)}">${formatTrendValue(row.coolingScore, "coolingScore")}</td>
          <td data-trend-field="coolingRateScore" class="${trendTableCellClass(row, "coolingRateScore", focusedColumnKey)}">${formatTrendValue(row.coolingRateScore, "coolingRateScore")}</td>
          <td data-trend-field="chargingScore" class="${trendTableCellClass(row, "chargingScore", focusedColumnKey)}">${formatTrendValue(row.chargingScore, "chargingScore")}</td>
          <td>${escapeHtml(row.findingTitle)}</td>
        </tr>
      `;
    }).join("");
    setRenderedTrendTableSignature(skeletonSignature);
    updateTrendEvidenceControlState(rows, focusedRowIds, focusedColumnKey);
    updateTrendTableFocusState(usableRows, focusedColumnKey, focusSignal, displayRows);
  }

  return {
    trendTableMetricColumnKey,
    trendTableCellClass,
    trendTableFocusSignal,
    setTrendEvidenceMode,
    setTrendEvidenceSort,
    setTrendEvidenceLayout,
    trendEvidenceScoreSort,
    trendEvidenceAttentionRows,
    trendEvidenceRowsForMode,
    sortTrendEvidenceRows,
    updateTrendEvidenceControlState,
    updateTrendTableFocusState,
    bindTrendTableEvents,
    updateTrendTableRowFocusInPlace,
    renderTrendTable,
  };
}
